import { Download } from 'lucide-react';

import { Button } from '@/components/ui/button';
import type { ReportCourse } from '@/hooks/queries/useReportCourses';

import CourseLink from './CourseLink';
import ReportTable, { type ReportColumn } from './ReportTable';
import { buildCatalogRows, downloadCsv, reportCsvFilename } from './reportUtils';

type CatalogReportProps = {
	courses: ReportCourse[];
	schoolId: string;
};

const COLUMNS: ReportColumn[] = [
	{ id: 'grade', label: 'Grade', sortType: 'grade' },
	{ id: 'discipline', label: 'Discipline' },
	{ id: 'division', label: 'Division' },
	{ id: 'course', label: 'Course' },
	{ id: 'ap', label: 'AP' },
	{ id: 'owners', label: 'Owners' },
	{ id: 'lastUpdated', label: 'Last Updated' },
	{ id: 'status', label: 'Status' },
];

const CatalogReport = ({ courses, schoolId }: CatalogReportProps) => {
	const rows = buildCatalogRows(courses);

	const handleDownload = () => {
		downloadCsv(
			reportCsvFilename('catalog', schoolId),
			['Grade', 'Discipline', 'Division', 'Course', 'AP', 'Owners', 'Last Updated', 'Status'],
			rows.map((row) => [
				row.grade,
				row.discipline,
				row.division,
				row.course,
				row.ap,
				row.owners,
				row.lastUpdated,
				row.status,
			])
		);
	};

	return (
		<div className="flex flex-col gap-4">
			<div className="flex flex-wrap items-center justify-between gap-3">
				<div>
					<h2 className="text-[16px] font-bold leading-5 text-(--header-text)">Course catalog</h2>
					<p className="mt-1 text-sm text-(--header-text)">
						{rows.length} course{rows.length === 1 ? '' : 's'}
					</p>
				</div>
				<Button type="button" variant="outline" size="small" onClick={handleDownload}>
					<Download size={14} className="mr-1.5" />
					Download CSV
				</Button>
			</div>
			<ReportTable
				columns={COLUMNS}
				rows={rows.map((row) => ({
					key: row.courseId,
					searchText: [
						row.grade,
						row.discipline,
						row.division,
						row.course,
						row.ap,
						row.owners,
						row.lastUpdated,
						row.status,
					].join(' '),
					sortValues: [
						row.grade,
						row.discipline,
						row.division,
						row.course,
						row.ap,
						row.owners,
						row.lastUpdated,
						row.status,
					],
					cells: [
						row.grade || '—',
						row.discipline || '—',
						row.division || '—',
						<CourseLink key="course" courseId={row.courseId}>
							{row.course}
						</CourseLink>,
						row.ap,
						row.owners || '—',
						row.lastUpdated || '—',
						row.status,
					],
				}))}
				emptyMessage="No courses found for the current filters."
				searchPlaceholder="Search catalog…"
			/>
		</div>
	);
};

export default CatalogReport;
